import React, { useState } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Wallet, Loader2, Check, Pencil } from 'lucide-react';
import { fmtCost } from './format';

export const BudgetCard = ({ budget, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(budget.monthly_budget_usd || '');
  const [saving, setSaving] = useState(false);
  const hasBudget = budget.monthly_budget_usd > 0;
  const pct = Math.min(100, budget.pct || 0);
  const barColor = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-emerald-500';

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.saveAiCostsSettings({ monthly_budget_usd: Number(value) || 0 });
      toast.success(r.data.monthly_budget_usd > 0 ? `Monatsbudget: ${fmtCost(r.data.monthly_budget_usd)}` : 'Budget entfernt');
      setEditing(false);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Budget konnte nicht gespeichert werden'); }
    setSaving(false);
  };

  return (
    <div className="bg-card border border-border rounded-sm p-4 flex flex-col gap-3" data-testid="ai-costs-budget-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Wallet className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span className="text-[10px] uppercase tracking-wider">Monatsbudget · {budget.month}</span>
        </div>
        {!editing && (
          <button onClick={() => { setValue(budget.monthly_budget_usd || ''); setEditing(true); }} data-testid="ai-costs-budget-edit-btn"
            className="text-[11px] text-sky-300 hover:underline inline-flex items-center gap-1"><Pencil className="w-3 h-3" /> {hasBudget ? 'Ändern' : 'Budget setzen'}</button>
        )}
      </div>
      {editing ? (
        <div className="flex items-center gap-2">
          <span className="text-sm font-mono text-muted-foreground">$</span>
          <input type="number" min="0" step="10" value={value} onChange={e => setValue(e.target.value)} data-testid="ai-costs-budget-input"
            className="w-32 bg-secondary border border-border rounded-sm px-2 py-1 text-sm font-mono text-foreground focus:outline-none focus:border-blue-500" placeholder="z. B. 200" />
          <button onClick={save} disabled={saving} data-testid="ai-costs-budget-save-btn"
            className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-sm px-2.5 py-1.5 disabled:opacity-50">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Speichern
          </button>
          <button onClick={() => setEditing(false)} className="text-xs text-muted-foreground hover:text-foreground" data-testid="ai-costs-budget-cancel-btn">Abbrechen</button>
        </div>
      ) : hasBudget ? (
        <>
          <div className="flex items-baseline gap-2">
            <span className={`text-2xl font-heading ${pct >= 100 ? 'text-red-400' : pct >= 80 ? 'text-amber-400' : 'text-foreground'}`} data-testid="ai-costs-budget-spent">{fmtCost(budget.month_spent)}</span>
            <span className="text-xs font-mono text-muted-foreground">von {fmtCost(budget.monthly_budget_usd)} · {Math.round(budget.pct || 0)} %</span>
          </div>
          <div className="h-1.5 w-full bg-secondary rounded-sm overflow-hidden">
            <div className={`h-full ${barColor} transition-[width] duration-500`} style={{ width: `${pct}%` }} data-testid="ai-costs-budget-bar" />
          </div>
        </>
      ) : (
        <div>
          <span className="text-2xl font-heading text-foreground" data-testid="ai-costs-budget-spent">{fmtCost(budget.month_spent)}</span>
          <p className="text-[11px] text-muted-foreground mt-0.5">in diesem Monat · kein Budget gesetzt</p>
        </div>
      )}
      <p className="text-[10px] text-muted-foreground">Admins erhalten eine Push-Nachricht bei 80 % und 100 % — KI-Funktionen laufen weiter.</p>
    </div>
  );
};
